import React from 'react'
import { Button, ConfigProvider, Flex, Form, Input } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import NewPasswordFields from './NewPasswordFields'
import { FORM_THEME } from './theme'

/**
 * First step of a password reset: ask Cognito to email the user a code.
 */
export function RequestResetForm({ onSubmit, onBack, busy, initialUsername }) {
  const intl = useIntl()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        initialValues={{ username: initialUsername }}
        onFinish={({ username }) => onSubmit({ username: username.trim() })}
        data-testid="login-reset-request-form"
      >
        <Form.Item
          name="username"
          label={<FormattedMessage id="auth.usernameLabel" />}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'auth.usernameRequired' }),
            },
          ]}
        >
          <Input
            data-testid="login-reset-username"
            placeholder={intl.formatMessage({ id: 'auth.usernamePlaceholder' })}
            autoComplete="username"
            autoFocus
          />
        </Form.Item>

        <Flex vertical={true} gap="small">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            data-testid="login-send-code"
            loading={busy}
          >
            <FormattedMessage id="auth.sendCode" />
          </Button>

          <Flex justify="center">
            <Button
              type="link"
              data-testid="login-back-to-sign-in"
              onClick={onBack}
            >
              <FormattedMessage id="auth.backToSignIn" />
            </Button>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  )
}

/**
 * Second step of a password reset: the emailed code and the new password.
 */
export function ConfirmResetForm({ onSubmit, onResend, onBack, busy }) {
  const intl = useIntl()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        onFinish={({ code, new_password }) =>
          onSubmit({ code: code.trim(), new_password })
        }
        data-testid="login-reset-confirm-form"
      >
        <Form.Item
          name="code"
          label={<FormattedMessage id="auth.codeLabel" />}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'auth.codeRequired' }),
            },
          ]}
        >
          <Input
            data-testid="login-reset-code"
            placeholder={intl.formatMessage({ id: 'auth.codePlaceholder' })}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
          />
        </Form.Item>

        <NewPasswordFields />

        <Flex vertical={true} gap="small">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            data-testid="login-reset-submit"
            loading={busy}
          >
            <FormattedMessage id="auth.resetPassword" />
          </Button>

          <Flex justify="space-between">
            <Button
              type="link"
              data-testid="login-resend-code"
              onClick={onResend}
            >
              <FormattedMessage id="auth.resendCode" />
            </Button>
            <Button
              type="link"
              data-testid="login-back-to-sign-in"
              onClick={onBack}
            >
              <FormattedMessage id="auth.backToSignIn" />
            </Button>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  )
}
